import { ArrowRight, Shield, Satellite, FileText, Calendar, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";

export default function NewHeroSection() {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full Background Image - Residential Home with Visible Roof */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1710139323182-69622cad5c3b?auto=format&fit=crop&w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      />
      
      {/* Dark gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40 z-0" />
      
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-0" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-20">
        <div className="grid xl:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full px-5 py-2 mb-8">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-300 font-medium">Trusted by 5,000+ DMV Homeowners</span>
            </div>

            {/* Main Headline */}
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-6 tracking-tight">
              Know Before You File.
              <br />
              <span className="text-emerald-400">
                Know Before You Settle.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-slate-300 max-w-xl mb-10 leading-relaxed">
              Protect your home with satellite roof technology, AI-powered estimate reviews, 
              and professional roofing expertise.
            </p>

            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-12">
              <Button
                onClick={() => scrollToSection("view-roof")}
                className="group bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-semibold px-8 py-6 text-lg transition-all duration-300 shadow-lg shadow-emerald-500/30"
                data-testid="hero-view-roof-btn"
              >
                <Satellite className="w-5 h-5 mr-2" />
                View My Roof
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button
                onClick={() => scrollToSection("upload-estimate")}
                variant="outline"
                className="group bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 rounded-full font-semibold px-6 py-6 text-lg transition-all duration-300"
                data-testid="hero-upload-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Upload Estimate
              </Button>
              
              <Button
                onClick={() => scrollToSection("roof-form")}
                variant="outline"
                className="group bg-emerald-500/20 backdrop-blur-sm border-2 border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/30 hover:border-emerald-400 rounded-full font-semibold px-6 py-6 text-lg transition-all duration-300"
                data-testid="hero-schedule-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Free Inspection
              </Button>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              {[
                "Satellite Roof Analysis",
                "AI Estimate Review",
                "Storm Damage Detection",
                "Insurance Claim Support"
              ].map((feature, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2"
                >
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-white/90">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Stats Card - Only show on XL screens */}
          <div className="hidden xl:block">
            <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/50 max-w-md ml-auto">
              <img
                src="https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=800&q=80"
                alt="Professional roof inspection"
                className="w-full h-64 object-cover rounded-2xl mb-6 shadow-lg"
              />
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: "10,000+", label: "Roofs Analyzed" },
                  { value: "98%", label: "Accuracy Rate" },
                  { value: "$2.5M+", label: "Claims Supported" },
                  { value: "4.9★", label: "Customer Rating" }
                ].map((stat, i) => (
                  <div key={i} className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="font-heading text-2xl text-emerald-600 mb-1">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
